import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAudioStore from '../store/audioStore';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import ReciterSelector from './ReciterSelector';

const GlobalPlayer = () => {
  const navigate = useNavigate();
  const [showReciterSelector, setShowReciterSelector] = useState(false);
  
  const {
    status,
    mode,
    currentSurah,
    currentAyah,
    surahData,
    currentTime,
    duration,
    progress,
    repeat,
    isExpanded,
    playbackSpeed,
    reciter,
    togglePlayPause,
    nextTrack,
    prevTrack,
    seek,
    toggleRepeat,
    setPlaybackSpeed,
    toggleExpanded,
    closePlayer,
  } = useAudioStore();
  
  // Keyboard shortcuts
  useKeyboardShortcuts({
    onPlayPause: togglePlayPause,
    onPrevious: prevTrack,
    onNext: nextTrack,
    onToggleRepeat: toggleRepeat,
    onEscape: () => {
      if (isExpanded) {
        toggleExpanded();
      }
    },
  });

  // Don't show if no audio loaded
  if (!mode || status === 'idle') {
    return null;
  }
  
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;
    seek(newTime);
  };
  
  const handleViewSurah = () => {
    if (currentSurah) {
      navigate(`/surah/${currentSurah}`);
    }
  };
  
  const getRepeatLabel = () => {
    switch (repeat) {
      case 'ayah': return '🔁 Ayah';
      case 'surah': return '🔁 Surah';
      default: return 'Repeat';
    }
  };
  
  const getModeLabel = () => {
    if (mode === 'full-surah') {
      return 'Full Surah';
    } else if (mode === 'single-ayah') {
      return `Ayah ${currentAyah}`;
    }
    return '';
  };
  
  if (isExpanded) {
    // Expanded Player
    return (
      <div 
        className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up"
        style={{
          background: 'var(--bg-overlay)',
          backdropFilter: 'blur(16px)',
          borderTop: '2px solid var(--gold-primary)',
          boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.3), 0 0 40px rgba(212, 175, 55, 0.2)',
          paddingBottom: 'var(--safe-area-bottom)',
        }}
      >
        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 
              className="text-lg font-semibold"
              style={{ color: 'var(--text-english)' }}
            >
              Now Playing
            </h3>
            <button
              onClick={toggleExpanded}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
              style={{
                background: 'rgba(148, 163, 184, 0.08)',
                color: 'var(--text-secondary)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(148, 163, 184, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(148, 163, 184, 0.08)';
              }}
            >
              Collapse
            </button>
          </div>
          
          {/* Surah Info */}
          <div className="text-center mb-8">
            {surahData && (
              <>
                <h2 
                  className="text-arabic text-4xl font-bold mb-3"
                  style={{ color: 'var(--text-arabic)' }}
                >
                  {surahData.name}
                </h2>
                <p 
                  className="text-2xl font-semibold mb-2"
                  style={{ color: 'var(--gold-hover)' }}
                >
                  {surahData.englishName}
                </p>
                <p 
                  className="text-sm mb-4"
                  style={{ color: 'var(--text-secondary)', opacity: 0.7 }}
                >
                  {surahData.englishNameTranslation}
                </p>
                <div className="flex gap-3 justify-center mt-4 flex-wrap">
                  <span 
                    className="text-xs px-3 py-1.5 rounded-full font-medium"
                    style={{
                      background: 'rgba(148, 163, 184, 0.08)',
                      color: 'var(--text-urdu)',
                      border: '1px solid var(--border-strong)',
                    }}
                  >
                    Surah {currentSurah}
                  </span>
                  <span 
                    className="text-xs px-3 py-1.5 rounded-full font-medium"
                    style={{
                      background: 'rgba(148, 163, 184, 0.08)',
                      color: 'var(--text-secondary)',
                      border: '1px solid var(--border-strong)',
                    }}
                  >
                    {surahData.numberOfAyahs} Verses
                  </span>
                </div>
              </>
            )}
            <p 
              className="text-sm mt-4"
              style={{ color: 'var(--text-secondary)', opacity: 0.6 }}
            >
              {getModeLabel()}
            </p>
          </div>
          
          {/* Progress Bar */}
          <div className="mb-6">
            <div 
              className="w-full h-3 rounded-full cursor-pointer transition-all"
              onClick={handleSeek}
              style={{
                background: 'var(--bg-secondary)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.height = '14px';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.height = '12px';
              }}
            >
              <div 
                className="h-full rounded-full transition-all duration-300"
                style={{ 
                  width: `${progress}%`,
                  background: 'linear-gradient(to right, var(--gold-primary), var(--gold-hover))',
                  boxShadow: '0 0 12px var(--gold-glow)',
                }}
              />
            </div>
            <div 
              className="flex justify-between mt-3 text-xs font-mono"
              style={{ color: 'var(--text-secondary)', opacity: 0.7 }}
            >
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>
          
          {/* Main Controls */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <button
              onClick={prevTrack}
              disabled={mode === 'full-surah' || (mode === 'single-ayah' && currentAyah === 1)}
              className="px-6 py-3 rounded-lg font-medium text-sm transition-all"
              style={{
                background: 'rgba(148, 163, 184, 0.08)',
                border: '1px solid var(--border-strong)',
                color: 'var(--text-urdu)',
                cursor: (mode === 'full-surah' || (mode === 'single-ayah' && currentAyah === 1)) ? 'not-allowed' : 'pointer',
                opacity: (mode === 'full-surah' || (mode === 'single-ayah' && currentAyah === 1)) ? 0.3 : 1,
              }}
            >
              ⏮ Previous
            </button>
            
            <button
              onClick={togglePlayPause}
              disabled={status === 'loading'}
              className="px-12 py-4 rounded-xl font-bold text-base transition-all shadow-lg"
              style={{
                background: status === 'loading' 
                  ? 'rgba(148, 163, 184, 0.3)'
                  : 'linear-gradient(to right, var(--gold-primary), var(--gold-hover))',
                color: 'white',
                cursor: status === 'loading' ? 'not-allowed' : 'pointer',
                boxShadow: '0 8px 24px var(--gold-glow)',
              }}
              onMouseEnter={(e) => {
                if (status !== 'loading') {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 12px 32px var(--gold-glow)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 8px 24px var(--gold-glow)';
              }}
            >
              {status === 'loading' ? 'Loading...' : status === 'playing' ? '⏸ Pause' : '▶ Play'}
            </button>
            
            <button
              onClick={nextTrack}
              disabled={mode === 'full-surah'}
              className="px-6 py-3 rounded-lg font-medium text-sm transition-all"
              style={{
                background: 'rgba(148, 163, 184, 0.08)',
                border: '1px solid var(--border-strong)',
                color: 'var(--text-urdu)',
                cursor: mode === 'full-surah' ? 'not-allowed' : 'pointer',
                opacity: mode === 'full-surah' ? 0.3 : 1,
              }}
            >
              Next ⏭
            </button>
          </div>
          
          {/* Playback Speed Control */}
          <div className="mb-6">
            <label 
              className="block text-xs font-medium mb-2 text-center"
              style={{ color: 'var(--text-secondary)', opacity: 0.6 }}
            >
              Playback Speed
            </label>
            <div 
              className="flex gap-1 p-1 rounded-lg max-w-xs mx-auto"
              style={{
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-default)',
              }}
            >
              {[0.75, 1.0, 1.25].map((speed) => (
                <button
                  key={speed}
                  onClick={() => setPlaybackSpeed(speed)}
                  className="flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all"
                  style={{
                    background: playbackSpeed === speed ? 'var(--gold-muted)' : 'transparent',
                    border: playbackSpeed === speed ? '2px solid var(--gold-primary)' : '1px solid transparent',
                    color: playbackSpeed === speed ? 'var(--gold-hover)' : 'var(--text-secondary)',
                  }}
                >
                  {speed}x
                </button>
              ))}
            </div>
          </div>

          {/* Secondary Controls */}
          <div className="flex items-center justify-center gap-3 flex-wrap mb-4">
            <button
              onClick={toggleRepeat}
              className="px-5 py-2.5 rounded-lg text-sm font-medium transition-all"
              style={{
                background: repeat !== 'off' ? 'var(--gold-muted)' : 'rgba(148, 163, 184, 0.08)',
                border: repeat !== 'off' ? '1px solid var(--border-gold)' : '1px solid var(--border-strong)',
                color: repeat !== 'off' ? 'var(--gold-hover)' : 'var(--text-secondary)',
              }}
            >
              {getRepeatLabel()}
            </button>
            
            <button
              onClick={() => setShowReciterSelector(true)}
              className="px-5 py-2.5 rounded-lg text-sm font-medium transition-all"
              style={{
                background: 'rgba(148, 163, 184, 0.08)',
                border: '1px solid var(--border-strong)',
                color: 'var(--text-urdu)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(148, 163, 184, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(148, 163, 184, 0.08)';
              }}
            >
              🎙️ Reciter
            </button>
            
            <button
              onClick={handleViewSurah}
              className="px-5 py-2.5 rounded-lg text-sm font-medium transition-all"
              style={{
                background: 'rgba(148, 163, 184, 0.08)',
                border: '1px solid var(--border-strong)',
                color: 'var(--text-urdu)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(148, 163, 184, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(148, 163, 184, 0.08)';
              }}
            >
              📖 View Surah
            </button>
            
            <button
              onClick={closePlayer}
              className="px-5 py-2.5 rounded-lg text-sm font-medium transition-all"
              style={{
                background: 'rgba(239, 68, 68, 0.1)',
                color: 'var(--error)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
              }}
            >
              Close Player
            </button>
          </div>
          
          {/* Reciter Info */}
          <p 
            className="text-xs text-center"
            style={{ color: 'var(--text-secondary)', opacity: 0.5 }}
          >
            Reciter: {reciter}
          </p>
        </div>
      </div>
    );
  }
  
  // Collapsed Player (Minimal)
  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up"
      style={{
        background: 'var(--bg-overlay)',
        backdropFilter: 'blur(12px)',
        borderTop: '2px solid var(--gold-primary)',
        boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.2), 0 0 30px rgba(212, 175, 55, 0.15)',
        paddingBottom: 'var(--safe-area-bottom)',
      }}
    >
      <div className="max-w-6xl mx-auto px-4 py-3">
        {/* Progress Bar - Top */}
        <div 
          className="w-full h-1 rounded-full cursor-pointer mb-3 transition-all"
          onClick={handleSeek}
          style={{
            background: 'var(--bg-secondary)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.height = '6px';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.height = '4px';
          }}
        >
          <div 
            className="h-full rounded-full transition-all duration-300"
            style={{ 
              width: `${progress}%`,
              background: 'linear-gradient(to right, var(--gold-primary), var(--gold-hover))',
            }}
          />
        </div>
        
        <div className="flex items-center gap-4">
          {/* Surah Info */}
          <button
            onClick={toggleExpanded}
            className="flex-1 min-w-0 text-left transition-opacity"
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.8';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            <h3 
              className="text-arabic text-lg font-bold truncate"
              style={{ color: 'var(--text-arabic)' }}
            >
              {surahData?.name || 'Loading...'}
            </h3>
            <p 
              className="text-sm truncate"
              style={{ color: 'var(--text-secondary)', opacity: 0.7 }}
            >
              {surahData?.englishName} • {getModeLabel()}
            </p>
          </button>
          
          {/* Time Display */}
          <div 
            className="hidden sm:block text-xs font-mono whitespace-nowrap"
            style={{ color: 'var(--text-secondary)', opacity: 0.6 }}
          >
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
          
          {/* Controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={prevTrack}
              disabled={mode === 'full-surah' || (mode === 'single-ayah' && currentAyah === 1)}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
              style={{
                background: 'rgba(148, 163, 184, 0.08)',
                border: '1px solid var(--border-strong)',
                color: 'var(--text-urdu)',
                cursor: (mode === 'full-surah' || (mode === 'single-ayah' && currentAyah === 1)) ? 'not-allowed' : 'pointer',
                opacity: (mode === 'full-surah' || (mode === 'single-ayah' && currentAyah === 1)) ? 0.3 : 1,
              }}
            >
              ⏮
            </button>
            
            <button
              onClick={togglePlayPause}
              disabled={status === 'loading'}
              className="px-8 py-2 rounded-lg font-semibold text-sm transition-all"
              style={{
                background: 'linear-gradient(to right, var(--gold-primary), var(--gold-hover))',
                color: 'white',
                cursor: status === 'loading' ? 'not-allowed' : 'pointer',
                opacity: status === 'loading' ? 0.5 : 1,
              }}
            >
              {status === 'loading' ? 'Loading' : status === 'playing' ? '⏸ Pause' : '▶ Play'}
            </button>
            
            <button
              onClick={nextTrack}
              disabled={mode === 'full-surah'}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
              style={{
                background: 'rgba(148, 163, 184, 0.08)',
                border: '1px solid var(--border-strong)',
                color: 'var(--text-urdu)',
                cursor: mode === 'full-surah' ? 'not-allowed' : 'pointer',
                opacity: mode === 'full-surah' ? 0.3 : 1,
              }}
            >
              ⏭
            </button>
            
            <button
              onClick={closePlayer}
              className="px-3 py-2 rounded-lg text-sm transition-all"
              style={{
                color: 'var(--error)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              ✕
            </button>
          </div>
        </div>
      </div>
      
      {/* Reciter Selector Modal */}
      <ReciterSelector 
        isOpen={showReciterSelector} 
        onClose={() => setShowReciterSelector(false)} 
      />
    </div>
  );
};

export default GlobalPlayer;
